import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AdminDataTable,
  type ColumnDef,
} from "@/components/admin/shared/AdminDataTable";
import { StatusBadge } from "@/components/admin/shared/StatusBadge";
import { YangoDispatchButton } from "./YangoDispatchButton";
import type {
  YangoDispatch,
  YangoDispatchStatus,
} from "@/lib/admin-api/yango";

// ---------------------------------------------------------------------------
// Status color mapping (extends the shared StatusBadge "delivery" variant)
// ---------------------------------------------------------------------------

const YANGO_STATUS_LABELS: Readonly<Record<YangoDispatchStatus, string>> = {
  pending: "en_attente",
  searching_driver: "en_cours",
  driver_assigned: "en_cours",
  pickup_arrived: "en_cours",
  in_transit: "en_cours",
  delivered: "livrée",
  cancelled: "échouée",
  failed: "échouée",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFcfa(value: number | null): string {
  if (value == null) return "—";
  return `${new Intl.NumberFormat("fr-SN").format(value)} FCFA`;
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface YangoDispatchListProps {
  readonly dispatches: readonly YangoDispatch[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
  readonly isLoading: boolean;
  readonly onPageChange: (page: number) => void;
  readonly onDispatchSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function YangoDispatchList({
  dispatches,
  pagination,
  isLoading,
  onPageChange,
  onDispatchSuccess,
}: YangoDispatchListProps) {
  const { t } = useTranslation("logistics");

  const statusLabel = useCallback(
    (status: YangoDispatchStatus): string => {
      const labelMap: Record<YangoDispatchStatus, string> = {
        pending: t("yango.status.pending"),
        searching_driver: t("yango.status.searching_driver"),
        driver_assigned: t("yango.status.driver_assigned"),
        pickup_arrived: t("yango.status.pickup_arrived"),
        in_transit: t("yango.status.in_transit"),
        delivered: t("yango.status.delivered"),
        cancelled: t("yango.status.cancelled"),
        failed: t("yango.status.failed"),
      };
      return labelMap[status] ?? status;
    },
    [t],
  );

  const columns: readonly ColumnDef<YangoDispatch>[] = useMemo(
    () => [
      {
        id: "order_ref",
        header: t("yango.col.order_ref"),
        accessorKey: "order_ref",
        sortable: true,
        mobileVisible: true,
      },
      {
        id: "customer_name",
        header: t("yango.col.customer"),
        accessorKey: "customer_name",
        sortable: true,
        mobileVisible: true,
      },
      {
        id: "delivery_address",
        header: t("yango.col.address"),
        accessorKey: "delivery_address",
        mobileVisible: false,
      },
      {
        id: "status",
        header: t("yango.col.status"),
        sortable: true,
        mobileVisible: true,
        cell: (row) => {
          const badgeStatus = YANGO_STATUS_LABELS[row.status] ?? "en_attente";
          return (
            <div className="flex flex-col gap-1">
              <StatusBadge status={badgeStatus} variant="delivery" />
              <span className="text-xs text-stone-400">
                {statusLabel(row.status)}
              </span>
            </div>
          );
        },
      },
      {
        id: "driver",
        header: t("yango.col.driver"),
        mobileVisible: false,
        cell: (row) =>
          row.driver_name ? (
            <div className="text-sm">
              <p className="font-medium text-stone-700">{row.driver_name}</p>
              <p className="text-xs text-stone-400">{row.driver_phone}</p>
            </div>
          ) : (
            <span className="text-xs text-stone-400">—</span>
          ),
      },
      {
        id: "cost_fcfa",
        header: t("yango.col.cost"),
        sortable: true,
        mobileVisible: true,
        cell: (row) => (
          <span className="font-medium text-stone-700">
            {formatFcfa(row.cost_fcfa)}
          </span>
        ),
      },
      {
        id: "created_at",
        header: t("yango.col.date"),
        sortable: true,
        mobileVisible: false,
        cell: (row) => (
          <span className="text-xs text-stone-500">
            {formatDate(row.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        mobileVisible: false,
        cell: (row) => {
          // Only show re-dispatch button for failed/cancelled dispatches
          if (row.status !== "failed" && row.status !== "cancelled") {
            return null;
          }
          return (
            <YangoDispatchButton
              orderId={row.order_id}
              orderRef={row.order_ref}
              onSuccess={onDispatchSuccess}
            />
          );
        },
      },
    ],
    [t, statusLabel, onDispatchSuccess],
  );

  return (
    <AdminDataTable<YangoDispatch>
      columns={columns}
      data={dispatches}
      pagination={pagination}
      onPageChange={onPageChange}
      isLoading={isLoading}
      emptyMessage={t("yango.empty")}
      getRowId={(row) => row.id}
    />
  );
}
